import { PAX_TYPE, PAX_TYPE_SHORT, CHECKIN_STATUS } from '@app/enums';
import { Reservation } from '../reservation.model';
import { PassengerIdMapEntry } from '@app/services/passenger-id-map.service';
import { normalizeAncillariesStatus } from '@utils/functions/ancillaries.utils';

export class ReqBoardingPass {
  public bookedLegCollection: any = [];
  public flightStatus: boolean;
  public reservationCode: string;

  constructor(pnr: Reservation) {
    this.bookedLegCollection = this.constructBookedReq(pnr);
    this.flightStatus = false;
    this.reservationCode = pnr.pnr;
  }

  private constructBookedReq(pnr: Reservation) {
    const flightSummary = pnr.flightSummary;
    let leg: any = {};
    let bookedLeg: any = [];

    leg.codeShare = false;
    leg.legCode = flightSummary.legCode;
    leg.segments = this.constructSegmentsReq(pnr);
    leg.legEntity = flightSummary.legEntity;
    leg.legRegion = flightSummary.legRegion;
    bookedLeg.push(leg);

    return bookedLeg;
  }

  private constructSegmentsReq(pnr: Reservation) {
    const flightSummary = pnr.flightSummary;
    const passengers = pnr.passengers;
    let segment: any;
    let segments = [];

    const paxType = {
      [PAX_TYPE.ADULT]: PAX_TYPE_SHORT.ADULT,
      [PAX_TYPE.CHILD]: PAX_TYPE_SHORT.CHILD,
      [PAX_TYPE.INFANT]: PAX_TYPE_SHORT.INFANT,
    };

    const seg = flightSummary.currentSegment;
    segment = {};
    segment.segmentCode = seg.segmentCode;
    segment.departureAirport = seg.departureAirport;
    segment.departureDateTime = seg.departureDateTime;
    segment.operatingCarrier = seg.operatingCarrier;
    segment.operatingFlightCode = seg.operatingFlightCode;
    let segmenst = flightSummary.segments.filter(
      (segment) =>
        segment.windowSegmentStatus === CHECKIN_STATUS.CKI && segment.operatingCarrier === 'AM'
    );

    // Fallback: si no hay segmentos CKI de AM, usar todos los segmentos CKI
    if (!segmenst.length) {
      segmenst = flightSummary.segments.filter(
        (segment) => segment.windowSegmentStatus === CHECKIN_STATUS.CKI
      );
    }
    for (let seg of segmenst) {
      segment = {};
      segment.segmentCode = seg.segmentCode;
      segment.departureAirport = seg.departureAirport;
      segment.departureDateTime = seg.departureDateTime;
      segment.operatingCarrier = seg.operatingCarrier;
      segment.operatingFlightCode = seg.operatingFlightCode;
      segment.windowSegmentStatus = seg.windowSegmentStatus || null;

      let filteredPassengers = passengers.filter((p) => p.checkinStatus && p.checkinSelected);

      // Fallback: si no hay pasajeros con checkinStatus, usar solo checkinSelected
      if (!filteredPassengers.length) {
        filteredPassengers = passengers.filter((p) => p.checkinSelected);
      }

      segment.passengers = filteredPassengers.map((p) => {
        // const seats = p?.seats;
        // const seat = seats.find(s=> s.segmentCode == seg.segmentCode);

        const seatNew = p?.seatsNew?.find((s) => s.segmentCode == seg.segmentCode);
        // Busca el asiento original del pasajero para este segmento
        const seatOld = p?.seats?.find((s) => s.segmentCode == seg.segmentCode);
        // Usa el asiento nuevo si existe, si no usa el original
        const anySeat = seatNew || seatOld;

        // Resolve passengerId with fallback from PassengerIdMapService (session backup)
        let resolvedPassengerId =
          p.passengerIds?.find((id) => id.segmentCode == seg.segmentCode)?.id ||
          p.passengerIds?.[0]?.id ||
          p.passengerId;

        if (!resolvedPassengerId) {
          const mapEntry = this.getPassengerIdFromMap(p.nameRefNumber);
          if (mapEntry) {
            resolvedPassengerId = mapEntry.passengerId;
          }
        }

        return {
          lastName: p.lastName,
          passengerId: resolvedPassengerId,
          passengerNameNumber: p.nameRefNumber,
          seatCode: anySeat?.seatCode || anySeat?.code || '',
          type: paxType[p.paxType],
          fareFamily: pnr.flightSummary.fareFamily || '',
          ancillaries: normalizeAncillariesStatus(p.ancillaries),
        };
      });

      segments.push(segment);
    }

    return segments;
  }

  /**
   * Reads the PassengerIdMapService data directly from sessionStorage
   * as a last-resort fallback when passenger.passengerId is missing.
   */
  private getPassengerIdFromMap(passengerNameNumber: string): PassengerIdMapEntry | null {
    try {
      const raw = sessionStorage.getItem('passengerIdMap');
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return null;
      }
      return parsed[passengerNameNumber] || null;
    } catch {
      return null;
    }
  }
}
